import React from 'react';
import ReactDOM from 'react-dom';
import { ThemeProvider } from 'styled-components';
import themes from '@codex/themes';
import RRProductItem from './richRelevanceItem';
import ProductPriceBlockWrapper, { VARIANT_FO_SITE_LISTING_PAGE_LIST }
  from '../../react/ProductPriceBlock/ProductPriceBlockWrapper';

/** Rich relevance grid autoresizer */
class RichRelevanceViewGrid {
  constructor(elementSelector) {
    this.containerElement = document.querySelector(elementSelector);
    this.resizeTimeout = 100;
    this.resizeTimerReference = 0;

    if (this.containerElement !== null) {
      // It's needed to wait until all images are loaded so the browser
      // can calculate heights in RR. Without this waiting, the progressive
      // enhancement (updateProductsHeight) was not working on
      // MS Surface + MS Edge correctly. It was setting wrong dimensions.

      const imgs = this.containerElement.querySelectorAll('img');
      const imgsCount = imgs.length;
      let counter = 0;
      const self = this;

      [].forEach.call(imgs, (img) => {
        if (img.complete && img.complete === true) {
          incrementCounter();
        } else {
          img.addEventListener('load', incrementCounter, false);
        }
      });

      /* eslint-disable no-inner-declarations */
      function incrementCounter() {
        if (++counter === imgsCount) {
          self.loadInnerElements();
        }
      }
      /* eslin-enable no-inner-declarations */
    }
  }

  replacePriceElements() {
    const priceElements = this.containerElement.getElementsByClassName('productPrices');
    priceElements.forEach((priceElement) => {
      const currentPriceElement = priceElement.querySelector('.currentPrice');
      if (currentPriceElement) {
        const currentPrice = currentPriceElement.textContent;
        ReactDOM.render(
          <ThemeProvider theme={themes.currys}>
            <ProductPriceBlockWrapper
              currentPrice={currentPrice}
              variant={VARIANT_FO_SITE_LISTING_PAGE_LIST}
            />
          </ThemeProvider>,
          priceElement
        );
      } else {
        // eslint-disable-next-line no-console
        console.warn(
          'No .currentPrice element found on a RichRelevance product, so it could not be replaced.'
        );
      }
    });
  }

  loadInnerElements() {
    let productItem;
    const productItems = this.containerElement.querySelectorAll('article.product');
    if (productItems.length > 0) {
      this.productItems = [];

      for (let i = 0; i < productItems.length; i++) {
        productItem = new RRProductItem(productItems[i]);
        this.productItems.push(productItem);
      }

      if (window.addEventListener) {
        window.addEventListener('resize', this.onResize.bind(this), false);
      }
      this.replacePriceElements();
      this.updateProductsHeight();
    }
  }

  /**
     * Handle resize event using debouncing
     */
  onResize() {
    clearTimeout(this.resizeTimerReference);
    this.resizeTimerReference = setTimeout(
      this.updateProductsHeight.bind(this),
      this.resizeTimeout
    );
  }

  /**
     * Updates products height per row
     */
  updateProductsHeight() {
    const productsCountInRow = this.getProductsCountInRow();
    let currentRow = [];
    let innerMaxHeights = [];
    let innerComponents;
    let cHeight;
    let product;

    // Goes through all the products
    for (let i = 0, productCount = this.productItems.length; i < productCount; i++) {
      product = this.productItems[i];

      currentRow.push(product);

      // Find max height of inner components per row
      innerComponents = product.getInnerComponents();
      for (let j = 0, cLen = innerComponents.length; j < cLen; j++) {
        cHeight = innerComponents[j].getHeight();
        if (innerMaxHeights[j] === undefined || innerMaxHeights[j] < cHeight) {
          innerMaxHeights[j] = cHeight;
        }
      }

      // If last product in row, set the heights and reset them
      if ((i + 1) % productsCountInRow === 0 || (i + 1) === productCount) {
        this.setRowHeights(currentRow, innerMaxHeights);
        currentRow = [];
        innerMaxHeights = [];
      }
    }
  }

  /**
     * Sets height of product and inner elements to the same height per row
     * @param {Array} row
     * @param {Array} innerHeights
     */
  setRowHeights(row, innerHeights) {
    let product;
    let innerComponents;

    for (let i = 0, len = row.length; i < len; i++) {
      product = row[i];
      innerComponents = product.getInnerComponents();

      for (let j = 0, cLen = innerComponents.length; j < cLen; j++) {
        if (innerHeights[j] > 0) {
          innerComponents[j].setHeight(innerHeights[j]);
        }
      }
    }
  }

  /**
     * Gets the width of the product
     * @returns {int}
     */
  getWidth() {
    return this.containerElement.offsetWidth;
  }

  /**
     * Gets the number of products in row
     * @returns {int}
     */
  getProductsCountInRow() {
    const firstProduct = this.productItems[0];
    const gridWidth = this.getWidth();
    const productWidth = firstProduct.getWidth();
    return Math.round(gridWidth / productWidth);
  }

  /**
     * Gets the last product in row
     * @param {element} product
     * @returns {element}
     */
  getLastInRow(product) {
    const productsCountInRow = this.getProductsCountInRow();
    let isFoundRow = false;
    let prod;

    if (product && product[0] !== undefined) {
      prod = product[0];
    } else {
      prod = product;
    }

    for (let i = 0, productCount = this.productItems.length; i < productCount; i++) {
      if (prod.id === this.productItems[i].getId()) {
        isFoundRow = true;
      }
      if (isFoundRow && (i + 1) % productsCountInRow === 0) {
        return this.productItems[i].getElement();
      }
    }

    return this.productItems[this.productItems.length - 1].getElement();
  }

  /**
     * Function called by RichRelevance`s code. Not called in our codebase.
     */
  static initCommonRR() {
    new RichRelevanceViewGrid('#rr_placement_0'); // eslint-disable-line no-new
    new RichRelevanceViewGrid('#rr_placement_1'); // eslint-disable-line no-new
  }
}

export default RichRelevanceViewGrid;
